import {
    CreditType,
    OrganizationCardState,
    useGetTotemCashHistoryQuery,
    WalletFragmentFragment,
} from 'data/__generated__';
import { PaymentMethodType } from './types';

import { DetailLink } from 'components/DetailsView';
import { TotemLabel } from 'components/TotemLabel';
import { CardAndMealVoucherPaymentMethods } from 'pages/Users/components/CardAndMealVoucherPaymentMethods';
import { Credits } from 'pages/Users/components/Credits';
import { Subventions } from 'pages/Users/components/Subventions';
import { TotemCashHistory } from 'pages/Users/components/TotemCashHistory';
import { WalletBalance } from 'pages/Users/components/WalletBalance';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Monté sous le verrou `CreditsRead` de la section « Moyens de paiement » : l'historique Totem Cash est chargé
// ici, sans bloquer ni faire tomber la fiche utilisateur.
export function PaymentMethods({ userId, wallet }: { userId: string; wallet: WalletFragmentFragment }) {
    const { creditCards, mealVouchers, subventions, credits, organizationCard } = wallet;
    const { data, error } = useGetTotemCashHistoryQuery({ variables: { userId, offset: 0, limit: 5 } });
    const paymentMethods: PaymentMethodType[] = [...(creditCards || []), ...(mealVouchers || [])];
    return (
        <>
            <WalletBalance credits={credits} />

            <TotemLabel>Moyens de paiement</TotemLabel>

            {organizationCard ? (
                <DetailLink
                    label="Carte organisation"
                    name={organizationCard.state === OrganizationCardState.Active ? 'Active' : 'Archivée'}
                    to={
                        organizationCard.organization ? `/organization/${organizationCard.organization._id}` : undefined
                    }
                />
            ) : null}

            <TablesGrid>
                {subventions && subventions.length > 0 ? (
                    <GridCell>
                        <TotemLabel>Subventions</TotemLabel>
                        <Subventions subventions={subventions} />
                    </GridCell>
                ) : null}

                {credits ? (
                    <GridCell>
                        <TotemLabel>Crédits Actifs (dont Totem Cash)</TotemLabel>
                        <Credits credits={credits} />
                    </GridCell>
                ) : null}

                {data?.totemCashHistory ? (
                    <GridCell>
                        <TotemLabel>
                            Historique Totem Cash (5 dernières recharges,{' '}
                            <SeeMoreLink to={`/credits?userId=${userId}&type=${CreditType.Cash}`}>
                                voir plus
                            </SeeMoreLink>
                            )
                        </TotemLabel>
                        <TotemCashHistory totemCashHistory={data.totemCashHistory} />
                    </GridCell>
                ) : error ? (
                    <GridCell>
                        <TotemLabel>Historique Totem Cash</TotemLabel>
                        <ErrorMessage>Erreur lors de la récupération de l'historique Totem Cash</ErrorMessage>
                    </GridCell>
                ) : null}

                <GridCell>
                    <TotemLabel>Cartes Bancaires et Titres Restaurants</TotemLabel>
                    <CardAndMealVoucherPaymentMethods paymentMethods={paymentMethods} />
                </GridCell>
            </TablesGrid>
        </>
    );
}

const TablesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-top: 8px;
`;

const GridCell = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const SeeMoreLink = styled(Link)`
    text-transform: lowercase;
    color: ${({ theme }) => theme.textColor};
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
`;
